'use client';

import React, { useState } from 'react';
import { Plus, Search, AlertTriangle, Clock, CheckCircle, Calendar, ChevronDown, ChevronUp, Package } from 'lucide-react';
import {
  MOCK_VACCINATIONS,
  VACCINE_STATUS_CONFIG,
  FLOCKS_LIST,
  VACCINE_INVENTORY,
  VaccinationRecord,
} from './healthData';

function VaccineStatusPill({ status }: { status: string }) {
  const cfg = VACCINE_STATUS_CONFIG[status] ?? { label: status, color: '#6b7280', bg: '#f9fafb' };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
      {(status === 'overdue' || status === 'missed') && <AlertTriangle size={10} />}
      {status === 'due' && <Clock size={10} />}
      {status === 'completed' && <CheckCircle size={10} />}
      {status === 'scheduled' && <Calendar size={10} />}
      {cfg.label}
    </span>
  );
}

function DueAlertBanner({ vaccinations }: { vaccinations: VaccinationRecord[] }) {
  const overdue = vaccinations.filter(v => v.status === 'overdue');
  const due = vaccinations.filter(v => v.status === 'due');
  if (overdue.length === 0 && due.length === 0) return null;

  return (
    <div className="mb-4 space-y-2">
      {overdue.length > 0 && (
        <div className="flex items-start gap-3 p-3 rounded-xl border border-red-200 bg-red-50">
          <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-red-800">
              {overdue.length} vaccination{overdue.length > 1 ? 's' : ''} overdue:
            </span>
            <span className="text-red-700 ml-1">
              {overdue.map(v => `${v.vaccine} for ${v.flock}`).join(' · ')}
            </span>
          </div>
        </div>
      )}
      {due.length > 0 && (
        <div className="flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50">
          <Clock size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-amber-800">
              {due.length} vaccination{due.length > 1 ? 's' : ''} due:
            </span>
            <span className="text-amber-700 ml-1">
              {due.map(v => `${v.vaccine} for ${v.flock} (${v.scheduledDate})`).join(' · ')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordVaccinationModal({ record, onClose }: { record: VaccinationRecord; onClose: () => void }) {
  const [birdsVaccinated, setBirdsVaccinated] = useState(String(record.birdsScheduled));
  const [batchNumber, setBatchNumber] = useState(record.batchNumber);
  const [notes, setNotes] = useState('');
  const [deductInventory, setDeductInventory] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Record Vaccination</h2>
            <p className="text-xs text-gray-500 mt-0.5">{record.scheduleNumber} · {record.vaccine}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 text-lg font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="p-3 rounded-lg bg-gray-50 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Flock:</span><span className="font-semibold text-gray-800">{record.flock} · {record.house}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Vaccine:</span><span className="font-semibold text-gray-800">{record.vaccine}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Route:</span><span className="font-semibold text-gray-800">{record.route}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Scheduled:</span><span className="font-semibold text-gray-800">{record.scheduledDate}</span></div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Birds Vaccinated *</label>
            <input required type="number" min="1" value={birdsVaccinated} onChange={e => setBirdsVaccinated(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            <p className="text-xs text-gray-400 mt-1">Scheduled: {record.birdsScheduled.toLocaleString()} birds</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Vaccine Batch Number</label>
            <input value={batchNumber} onChange={e => setBatchNumber(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>

          {/* Inventory deduction */}
          <div className="p-3 rounded-lg border border-green-100 bg-green-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package size={14} className="text-green-700" />
                <span className="text-xs font-semibold text-green-800">Deduct from Inventory</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={deductInventory} onChange={e => setDeductInventory(e.target.checked)} className="sr-only peer" />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
            {deductInventory && (
              <div className="text-xs text-green-700 space-y-0.5">
                <div><span className="font-medium">Item:</span> {record.inventoryItemName}</div>
                <div><span className="font-medium">Doses to deduct:</span> {Number(birdsVaccinated).toLocaleString()} doses ({(Number(birdsVaccinated) / 1000).toFixed(2)} × 1000-dose vials)</div>
                <div><span className="font-medium">Current stock:</span> {VACCINE_INVENTORY.find(v => v.id === record.inventoryItemId)?.currentQty ?? '—'} {VACCINE_INVENTORY.find(v => v.id === record.inventoryItemId)?.unit}</div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Any observations during vaccination..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: 'var(--primary)' }}>
              Confirm Vaccination
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddScheduleModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    flock: '', vaccine: '', disease: '', scheduledDate: '',
    recommendedAgeDays: '', route: '', dosePerBird: '1', notes: '',
  });
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Schedule Vaccination</h2>
            <p className="text-xs text-gray-500 mt-0.5">Add a new vaccination to the schedule</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 text-lg font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Flock *</label>
              <select required value={form.flock} onChange={e => set('flock', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select flock...</option>
                {FLOCKS_LIST.map(f => (
                  <option key={f.id} value={f.id}>{f.number} — {f.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Vaccine *</label>
              <select required value={form.vaccine} onChange={e => set('vaccine', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select vaccine from inventory...</option>
                {VACCINE_INVENTORY.map(v => (
                  <option key={v.id} value={v.id}>{v.name} (Stock: {v.currentQty} {v.unit})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Disease Targeted *</label>
              <input required value={form.disease} onChange={e => set('disease', e.target.value)}
                placeholder="e.g. Newcastle Disease"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Recommended Age (days)</label>
              <input type="number" min="0" value={form.recommendedAgeDays} onChange={e => set('recommendedAgeDays', e.target.value)}
                placeholder="e.g. 14"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Scheduled Date *</label>
              <input required type="date" value={form.scheduledDate} onChange={e => set('scheduledDate', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Administration Route</label>
              <select value={form.route} onChange={e => set('route', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select route...</option>
                <option>Eye drop</option>
                <option>Drinking water</option>
                <option>Subcutaneous injection</option>
                <option>Intramuscular injection</option>
                <option>Spray</option>
                <option>Wing web stab</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                placeholder="Special instructions..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: 'var(--primary)' }}>
              Schedule Vaccination
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function VaccinationTab() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [recordingVac, setRecordingVac] = useState<VaccinationRecord | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = MOCK_VACCINATIONS.filter(v => {
    const matchSearch = search === '' ||
      v.flock.toLowerCase().includes(search.toLowerCase()) ||
      v.vaccine.toLowerCase().includes(search.toLowerCase()) ||
      v.scheduleNumber.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || v.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const sortOrder: Record<string, number> = { overdue: 0, due: 1, scheduled: 2, completed: 3, missed: 4 };
  const sorted = [...filtered].sort((a, b) => (sortOrder[a.status] ?? 5) - (sortOrder[b.status] ?? 5));

  return (
    <div>
      {showAddModal && <AddScheduleModal onClose={() => setShowAddModal(false)} />}
      {recordingVac && <RecordVaccinationModal record={recordingVac} onClose={() => setRecordingVac(null)} />}

      <DueAlertBanner vaccinations={MOCK_VACCINATIONS} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by flock, vaccine, schedule number..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="all">All Statuses</option>
          <option value="overdue">Overdue</option>
          <option value="due">Due</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="missed">Missed</option>
        </select>
        <button onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={15} /> Schedule
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100" style={{ backgroundColor: '#f8fafc' }}>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Schedule #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vaccine / Disease</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Flock / House</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Scheduled Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Birds</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Inventory</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400 text-sm">No vaccination records found.</td></tr>
              )}
              {sorted.map(vac => (
                <React.Fragment key={vac.id}>
                  <tr className={`hover:bg-gray-50 transition-colors ${vac.status === 'overdue' ? 'bg-red-50/30' : vac.status === 'due' ? 'bg-amber-50/20' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-700">{vac.scheduleNumber}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-800 text-xs">{vac.vaccine}</div>
                      <div className="text-xs text-gray-400">{vac.disease}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-800 text-xs">{vac.flock}</div>
                      <div className="text-xs text-gray-400">{vac.house} · {vac.farm}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-700 font-medium">{vac.scheduledDate}</div>
                      {vac.actualDate && <div className="text-xs text-green-600">Done: {vac.actualDate}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      <div>{vac.birdsScheduled.toLocaleString()} sched.</div>
                      {vac.birdsVaccinated && <div className="text-green-600">{vac.birdsVaccinated.toLocaleString()} done</div>}
                    </td>
                    <td className="px-4 py-3"><VaccineStatusPill status={vac.status} /></td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-600 truncate max-w-[120px]">{vac.inventoryItemName}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {(vac.status === 'due' || vac.status === 'overdue' || vac.status === 'scheduled') && (
                          <button onClick={() => setRecordingVac(vac)}
                            className="px-2 py-1 text-xs font-semibold rounded-lg text-white transition-colors"
                            style={{ backgroundColor: 'var(--primary)' }}>
                            Record
                          </button>
                        )}
                        <button onClick={() => setExpandedId(expandedId === vac.id ? null : vac.id)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400">
                          {expandedId === vac.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === vac.id && (
                    <tr className="bg-blue-50/20">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Administration</div>
                            <div className="text-gray-600">Route: {vac.route}</div>
                            <div className="text-gray-600">Dose: {vac.dosePerBird} {vac.doseUnit}/bird</div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Vaccine Batch</div>
                            <div className="text-gray-600">{vac.batchNumber}</div>
                            <div className="text-gray-500">Exp: {vac.expiryDate}</div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Personnel</div>
                            <div className="text-gray-600">By: {vac.administeredBy}</div>
                            <div className="text-gray-600">Vet: {vac.veterinarian}</div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Next Dose</div>
                            {vac.nextDoseDate ? (
                              <>
                                <div className="text-gray-600">{vac.nextDoseDate}</div>
                                <div className="text-gray-500 text-xs">{vac.nextDoseVaccine}</div>
                              </>
                            ) : (
                              <div className="text-gray-400">No next dose scheduled</div>
                            )}
                          </div>
                          {vac.notes && (
                            <div className="col-span-2 md:col-span-4">
                              <div className="font-semibold text-gray-700 mb-1">Notes</div>
                              <div className="text-gray-600">{vac.notes}</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">{sorted.length} vaccination record{sorted.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}
